import React, { useContext, useEffect, useState } from 'react';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import whatsappIcon from '../../assets/Whatsapp.svg';
import styles from '../../assets/styles/componentStyles/voiceAgentStyles/voiceAgent.module.scss';
import globalStyles from '../../assets/styles/global.module.scss';
import { useRuleBasedInboxStore } from '../../store/ruleBasedInboxStore';
import { useVoiceCallStore } from '../../store/voiceCallStore';
import { useGenericStore } from '../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import { UserContext, UserContextPayload } from '../../context/userContext';
import agent from '../../services/api';
import { useInboundCallRingAlert } from '../../hooks/useInboundCallRingAlert';
import {
    getBusinessPhoneLabel,
    getEndUserDisplayName,
    getEndUserPhoneNumber,
    resolveInboundCallIds,
} from '../../utils/voiceCallDetails';
import { formatPhoneNumber } from '../../pages/ruleBasedInbox/utils';

const GlobalInboundCallNotification = () => {
    const { handleError } = useContext(UserContext) as UserContextPayload;
    const { startInboundCallAlert, stopInboundCallAlert } = useInboundCallRingAlert();

    const getWabaNameAndPhoneNumberBytIntegratedChannelId = useRuleBasedInboxStore((state) => state.getWabaNameAndPhoneNumberBytIntegratedChannelId);

    const { voiceCallId, setVoiceCallId, flagInboundCallNotification, callingWabaDetails, callingPersonDetails, inboundCallExecutionData, voiceCallDetails, openInboundCallPopupFromNotification, clearInboundCallState } = useVoiceCallStore(useShallow((state) => ({
        voiceCallId: state.voiceCallId,
        setVoiceCallId: state.setVoiceCallId,
        flagInboundCallNotification: state.flagInboundCallNotification,
        callingWabaDetails: state.callingWabaDetails,
        callingPersonDetails: state.callingPersonDetails,
        inboundCallExecutionData: state.inboundCallExecutionData,
        voiceCallDetails: state.voiceCallDetails,
        openInboundCallPopupFromNotification: state.openInboundCallPopupFromNotification,
        clearInboundCallState: state.clearInboundCallState
    })));

    const { showSuccess, showError } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess,
        showError: state.showError,
    })));

    const [isRejectingCall, setIsRejectingCall] = useState(false);
    const [isAcceptingFromNotification, setIsAcceptingFromNotification] = useState(false);

    const findPersonDetail = (field: string) => {
        const personMetaData = callingPersonDetails?.thread_metadata;

        return personMetaData?.find(
            (data) => data?.metadata_type_name?.toLowerCase() === field?.toLowerCase()
        )?.metadata_value;
    };

    const callerName = getEndUserDisplayName(voiceCallDetails, findPersonDetail('name'));
    const callerPhone = getEndUserPhoneNumber(voiceCallDetails, findPersonDetail('phone_number'));
    const receivingOnLabel = getBusinessPhoneLabel(voiceCallDetails, callingWabaDetails, 'inbound');

    const getCallerInitial = () => {
        if (!callerName || callerName === 'Unknown Caller') return null;
        const firstChar = callerName.trim().charAt(0);
        return firstChar ? firstChar.toUpperCase() : null;
    };

    const getInboundCallIds = () => resolveInboundCallIds({
        inboundCallExecutionData,
        voiceCallId,
        callingPersonDetails,
        callingWabaDetails,
        voiceCallDetails,
        getWabaNameAndPhoneNumberBytIntegratedChannelId,
    });

    const handleDismissNotification = () => {
        stopInboundCallAlert();
        clearInboundCallState();
        setVoiceCallId('');
    };

    const handleAcceptNotification = () => {
        stopInboundCallAlert();
        setIsAcceptingFromNotification(true);
        openInboundCallPopupFromNotification();
        setTimeout(() => {
            setIsAcceptingFromNotification(false)
        }, 3000);
    };

    const handleDeclineNotification = async () => {
        const { callId, phoneNumberId } = getInboundCallIds();

        if (!callId) {
            showError('Call details are missing. Unable to reject the call.');
            clearInboundCallState();
            setVoiceCallId('');
            stopInboundCallAlert();
            return;
        }

        try {
            setIsRejectingCall(true);
            stopInboundCallAlert();

            const res = await agent.acceptIncomingCall.post({
                call_id: callId,
                phone_number_id: phoneNumberId,
                flag_pickup: false,
            });

            if (!res?.status) {
                handleError(res);
                showError(res?.error ?? 'Failed to decline the call');
            } else {
                showSuccess(res?.error ?? 'Inbound call declined');
            }
        } catch (err: any) {
            console.error('Failed to decline inbound call:', err);
            showError(err?.message ?? 'Failed to decline the call');
        } finally {
            setIsRejectingCall(false);
            clearInboundCallState();
            setVoiceCallId('');
        }
    };

    useEffect(() => {
        if (flagInboundCallNotification) {
            startInboundCallAlert();
        } else {
            stopInboundCallAlert();
        }

        return () => stopInboundCallAlert();
    }, [flagInboundCallNotification, startInboundCallAlert, stopInboundCallAlert]);

    const isNotificationBusy = isRejectingCall || isAcceptingFromNotification;

    if (!flagInboundCallNotification) return null;

    return (
        <div className={styles.inboundCallNotification} role="alert" aria-live="assertive">
            <button
                type="button"
                className={styles.inboundCallNotificationCloseBtn}
                onClick={handleDismissNotification}
                disabled={isNotificationBusy}
                aria-label="Dismiss notification"
            >
                <CloseIcon sx={{ fontSize: 18 }} />
            </button>

            <div className={styles.inboundCallNotificationHeader}>
                <div className={styles.inboundCallNotificationBrand}>
                    <img alt="whatsapp" src={whatsappIcon} height={18} width={18} className={globalStyles.displayFlexStyle} />
                    <span>Incoming WhatsApp call</span>
                </div>
                <span className={styles.inboundCallNotificationRinging}>Ringing</span>
            </div>

            <div className={styles.inboundCallNotificationBody}>
                <div className={styles.inboundCallNotificationAvatar}>
                    {getCallerInitial() ? getCallerInitial() : <PersonIcon sx={{ color: '#fff', fontSize: 22 }} />}
                </div>
                <div className={styles.inboundCallNotificationDetails}>
                    <div className={styles.inboundCallNotificationName}>{callerName}</div>
                    {callerPhone && <div className={styles.inboundCallNotificationPhone}>{formatPhoneNumber(callerPhone)}</div>}
                    {receivingOnLabel && <div className={styles.inboundCallNotificationWaba}>{receivingOnLabel}</div>}
                </div>
            </div>

            <div className={styles.inboundCallNotificationActions}>
                <button
                    type="button"
                    className={`${styles.inboundCallNotificationActionBtn} ${styles.inboundCallNotificationDeclineBtn}`}
                    // onClick={() => !isNotificationBusy && handleDeclineNotification()}
                    onClick={() => handleDeclineNotification()}
                    disabled={isNotificationBusy}
                    aria-label="Decline call"
                >
                    <CallEndIcon sx={{ fontSize: 22 }} />
                </button>
                <button
                    type="button"
                    className={`${styles.inboundCallNotificationActionBtn} ${styles.inboundCallNotificationAcceptBtn}`}
                    onClick={handleAcceptNotification}
                    disabled={isNotificationBusy}
                    aria-label="Accept call"
                >
                    <CallIcon sx={{ fontSize: 22 }} />
                </button>
            </div>
        </div>
    );
};

export default GlobalInboundCallNotification;
